import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {Settings} from '../../settings.class';
import {HaversineService} from '../../haversine.service';
import {ApiService} from '../../services/api/api.service';
import {Hectometre} from '../../hectometre';
import {map} from 'rxjs/operators';
import {RouteSimplifier} from '../../route-simplifier';

export interface SnapToRoadResult {
    snappedPoints: { lat: number, lng: number }[];
    totalLength: number;
}

@Injectable()
export class LocationService {

    constructor(
        private http: HttpClient,
        private apiService: ApiService,
        private haversineService: HaversineService) {
    }

    getHecto(way: string) {
        return this.apiService.getCall$<Hectometre[]>('hecto', {way});
    }

    searchHecto(search: string) {
        return this.apiService.postCall$<Hectometre[]>('search', {search});
    }

    getLocations(lat: string | number, lng: string | number): Observable<any> {
        return this.apiService.getCallData$<GoogleGeocodingResponse>('geocode/reverse-geocode', {lat, lng}).pipe(map(res => {
            return {
                resourceSets: [{
                    estimatedTotal: 999,
                    resources: res.results.filter(qq => qq.types.includes('street_address')).map(r => {
                        const street = r.address_components.find(ac => ac.types.includes('route'))?.long_name;
                        const houseNumber = r.address_components.find(ac => ac.types.includes('street_number'))?.long_name;
                        const zipcode = r.address_components.find(ac => ac.types.includes('postal_code'))?.long_name;
                        const town = r.address_components.find(ac => ac.types.includes('locality'))?.long_name;
                        return {
                            address: {
                                formattedAddress: r.formatted_address,
                                addressLine: `${street} ${houseNumber}`,
                                street: street,
                                housenumber: houseNumber,
                                postalCode: zipcode,
                                zipcode: zipcode,
                                town: town,
                                locality: town
                            }
                        };
                    })
                }]
            }
        }));
    }

    getAddresses(term: string) {
        term += ' Nederland';
        return this.apiService.getCallData$<GoogleGeocodingResponse>('geocode/geocode', {address: term}).pipe(map(res => {
            return {
                resourceSets: [{
                    estimatedTotal: 999,
                    resources: res.results.map(r => {
                        const street = r.address_components.find(ac => ac.types.includes('route'))?.long_name;
                        const houseNumber = r.address_components.find(ac => ac.types.includes('street_number'))?.long_name;
                        const zipcode = r.address_components.find(ac => ac.types.includes('postal_code'))?.long_name;
                        const town = r.address_components.find(ac => ac.types.includes('locality'))?.long_name;
                        return {
                            name: r.formatted_address,
                            point: {
                                coordinates: [
                                    r.geometry.location.lat,
                                    r.geometry.location.lng
                                ]
                            },
                            address: {
                                formattedAddress: r.formatted_address,
                                addressLine: `${street} ${houseNumber}`,
                                street: street,
                                housenumber: houseNumber,
                                postalCode: zipcode,
                                zipcode: zipcode,
                                town: town,
                                locality: town
                            }
                        };
                    })
                }]
            }
        }));
    }

    getSnappedRoute(
        points: { lat: number, lng: number }[],
        travelModeWalking?: boolean
    ): Observable<{ snappedPoints: any[], totalLength: number, simplePoints?: { lat: number; lng: number }[] }> {
        return new Observable(observer => {
            if (points.length < 2) {
                observer.next({snappedPoints: points, totalLength: 0});
                observer.complete();
                return;
            }
            if (points.length > 24) {
                const routePoints = new RouteSimplifier(points);
                points = routePoints.getSimplifiedMaxPoints();
            }

            const directionsService = new google.maps.DirectionsService();
            const origin = new google.maps.LatLng(points[0].lat, points[0].lng);
            const destination = new google.maps.LatLng(points[points.length - 1].lat, points[points.length - 1].lng);
            const waypoints = points.slice(1, -1).map(p => ({
                location: new google.maps.LatLng(p.lat, p.lng),
                stopover: false,
            }));
            directionsService.route(
                {
                    origin,
                    destination,
                    waypoints,
                    travelMode: travelModeWalking ? google.maps.TravelMode.WALKING : google.maps.TravelMode.DRIVING,
                },
                (result, status) => {
                    if (status !== 'OK' || !result.routes[0]) {
                        observer.next({snappedPoints: points, totalLength: 0});
                        observer.complete();
                        return;
                    }
                    // Polyline path ophalen
                    const overviewPath = result.routes[0].overview_path;
                    const pathParam = overviewPath.map((latLng: any) => `${latLng.lat()},${latLng.lng()}`).join('|');

                    observer.next({
                        snappedPoints: overviewPath.map((latLng: any) => ({lat: latLng.lat(), lng: latLng.lng()})),
                        totalLength: result.routes[0].legs[0].distance.value,
                        simplePoints: points
                    });
                }
            );
        });
    }


    getSnappedPoints(points, travelModeWalking?: boolean): Observable<any> {
        return new Observable(observer => {
            const resolveWithDistance = (parsedSnappedPoints) => {

                let totalMeters = 0;
                parsedSnappedPoints.forEach((point, index) => {
                    if (index > 0) {
                        const meter = this.haversineService.getDistanceInMeters(
                            parsedSnappedPoints[index - 1],
                            point
                        );
                        totalMeters += meter;
                    }
                });

                observer.next({
                    snappedPoints: parsedSnappedPoints,
                    totalLength: Math.round(totalMeters)
                });
            };
            const url = `https://dev.virtualearth.net/REST/v1/Routes/SnapToRoad?key=${Settings.BING_MAPS_API_KEY}`;
            this.http.post(url, {
                'points': points,
                'interpolate': true,
                'travelMode': travelModeWalking ? 'walking' : 'driving'
            }).subscribe((data) => {
                if (data['resourceSets'].length > 0) {
                    const snappedPoints = data['resourceSets'][0]['resources'][0]['snappedPoints'];
                    const parsedSnappedPoints = snappedPoints.map((p) => {
                        return {
                            latitude: p.coordinate.latitude,
                            longitude: p.coordinate.longitude
                        };
                    });

                    if (parsedSnappedPoints.length < 2) {
                        resolveWithDistance(points);
                    } else {
                        resolveWithDistance(parsedSnappedPoints);
                    }
                } else {
                    resolveWithDistance(points);
                }

            }, () => {
                resolveWithDistance(points);
            });
        });
    }
}

export interface Address {
    street;
    housenumber;
    zipcode;
    town;
}

export interface GoogleGeocodingResponse {
    results: GoogleGeocodingResult[];
    status: string;
}

export interface GoogleGeocodingResult {
    address_components: AddressComponent[];
    formatted_address: string;
    geometry: {
        location: {
            lat: number;
            lng: number;
        };
        location_type: string;
        viewport: {
            northeast: LatLng;
            southwest: LatLng;
        };
    };
    place_id: string;
    types: string[];
}

export interface AddressComponent {
    long_name: string;
    short_name: string;
    types: string[];
}

export interface LatLng {
    lat: number;
    lng: number;
}
